import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  useLazyGetCustomersListQuery,
  useDeleteCustomerMutation,
} from '../store/api';
import { extractList, extractPagination } from '../utils/response';
import { useDebounce } from '../hooks/useDebounce';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Button,
  TablePagination,
  Avatar,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { CREATE_CUSTOMER } from '../navigations/path';
import SweetAlert from '../utils/sweetAlert';
import { useAuth } from '../hooks/useAuth';
import Can from './permissions/Can';
import { getErrorMessage } from '../utils/errorHandler';

const CustomerTableList = () => {
  const [fetchCustomersList] = useLazyGetCustomersListQuery();
  const [deleteCustomer] = useDeleteCustomerMutation();
  const { hasPermission, hasAnyPermission } = useAuth();

  const canViewCustomerList = hasAnyPermission([
    'customer-list',
    'customer-list-front',
  ]);
  const canEditCustomer = hasPermission('customer-edit');
  const canViewCustomer = hasPermission('customer-view');
  const canDeleteCustomer = hasPermission('customer-delete');

  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuCustomer, setActionMenuCustomer] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const fetchCustomers = useCallback(async () => {
    if (!canViewCustomerList) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const params = {
        'relations[]': ['status'],
        paginate: 1,
        page: page + 1,
        per_page: rowsPerPage,
      };
      const q = debouncedSearch.trim();
      if (q) params.search = q;

      const customerData = await fetchCustomersList(params).unwrap();
      const list = extractList(customerData);
      const pag = extractPagination(customerData);
      setCustomers(Array.isArray(list) ? list : []);
      setTotal(Number(pag.total) || 0);
      setError(null);
    } catch (err) {
      console.error('Fetch customers error:', err);
      setError(
        err?.data?.message ||
          getErrorMessage(err) ||
          'Failed to load customers. Please check your network or API.'
      );
      setCustomers([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [canViewCustomerList, fetchCustomersList, page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    if (!canViewCustomerList) {
      toast.error('You do not have permission to view customers.');
      return;
    }
    fetchCustomers();
  }, [canViewCustomerList, fetchCustomers]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (customerId) => {
    if (!canEditCustomer) return;
    navigate(`${CREATE_CUSTOMER}?edit_id=${customerId}`);
  };

  const handleView = (customerId) => {
    if (!canViewCustomer) return;
    navigate(`${CREATE_CUSTOMER}?view=${customerId}`);
  };

  const handleDelete = async (customerId) => {
    if (!canDeleteCustomer) return;
    try {
      const target = customers.find((c) => c.id === customerId);
      const result = await SweetAlert.deleteConfirm(target?.name || 'This Customer');

      if (!result.isConfirmed) return;

      await deleteCustomer(customerId).unwrap();
      await SweetAlert.success({
        text: 'Customer record deleted successfully.',
      });
      await fetchCustomers();
    } catch (err) {
      console.error('Delete action error:', err);
      await SweetAlert.error({
        text:
          err?.data?.message ||
          getErrorMessage(err) ||
          'Failed to delete customer',
      });
    }
  };

  const openActionMenu = (event, customer) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuCustomer(customer);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuCustomer(null);
  };

  const handleMenuView = () => {
    if (actionMenuCustomer) handleView(actionMenuCustomer.id);
    closeActionMenu();
  };

  const handleMenuEdit = () => {
    if (actionMenuCustomer) handleEdit(actionMenuCustomer.id);
    closeActionMenu();
  };

  const handleMenuDelete = () => {
    if (actionMenuCustomer) handleDelete(actionMenuCustomer.id);
    closeActionMenu();
  };

  const getStatusChip = (status) => {
    const normalizedStatus = status ? status.toLowerCase() : 'inactive';
    const color = normalizedStatus === 'active' ? 'success' : 'error';
    const label = normalizedStatus === 'active' ? 'Active' : 'Inactive';
    return (
      <Chip
        label={label}
        color={color}
        size="small"
        sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
      />
    );
  };

  const formatGender = (gender) => {
    switch (gender) {
      case 'M':
        return 'Male';
      case 'F':
        return 'Female';
      case 'O':
        return 'Other';
      default:
        return 'Not Specified';
    }
  };

  const formatDob = (dob) => {
    if (!dob) return '—';
    const str = String(dob);
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
    return str;
  };

  const avatarSrc = (customer) =>
    customer?.avatar?.path ||
    customer?.avatar?.url ||
    customer?.avatar_url ||
    '';

  return (
    <Box sx={{ flexGrow: 1, p: 4, bgcolor: '#fff' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Customers List ({total})
        </Typography>
        <Can permission="customer-create">
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(CREATE_CUSTOMER)}
            disabled={isLoading}
          >
            Add New Customer
          </Button>
        </Can>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 3,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center',
          p: 2,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <TextField
          label="Search Name or Email"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 250 }}
          disabled={isLoading}
        />
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress color="primary" />
        </Box>
      )}
      {!isLoading && error && (
        <Typography color="error" align="center" sx={{ my: 4 }}>
          {error}
        </Typography>
      )}
      {!isLoading && !error && total === 0 && (
        <Typography align="center" sx={{ my: 4 }}>
          No customers found matching your criteria.
        </Typography>
      )}

      {!isLoading && !error && total > 0 && (
        <Paper elevation={1} sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table stickyHeader aria-label="customer list table">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: 'primary.main',
                    '& th': { color: 'black', fontWeight: 'bold' },
                  }}
                >
                  <TableCell>Avatar</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>DOB</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Avatar
                        src={avatarSrc(customer) || undefined}
                        alt={customer.name}
                        sx={{ width: 36, height: 36 }}
                      >
                        {(customer.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                    </TableCell>
                    <TableCell>{customer.name}</TableCell>
                    <TableCell>{customer.email}</TableCell>
                    <TableCell>{customer.username ?? '—'}</TableCell>
                    <TableCell>{customer.phone ?? '—'}</TableCell>
                    <TableCell>{formatDob(customer.dob)}</TableCell>
                    <TableCell>
                      {getStatusChip(customer.status?.name)}
                    </TableCell>
                    <TableCell>{formatGender(customer.gender)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        aria-label="Actions"
                        onClick={(e) => openActionMenu(e, customer)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Menu
            anchorEl={actionMenuAnchor}
            open={Boolean(actionMenuAnchor)}
            onClose={closeActionMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: 120 } }}
          >
            <Can permission="customer-view">
              <MenuItem onClick={handleMenuView}>
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>View</ListItemText>
              </MenuItem>
            </Can>
            <Can permission="customer-edit">
              <MenuItem onClick={handleMenuEdit}>
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit</ListItemText>
              </MenuItem>
            </Can>
            <Can permission="customer-delete">
              <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
                <ListItemIcon>
                  <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            </Can>
          </Menu>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </Box>
  );
};

export default CustomerTableList;
